import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { DateTime } from "luxon";

import { useInstallation } from "../../../components/InstallationContext";
import LoadingIndicator from "../../../components/LoadingIndicator";
import { GraphqlErrorMessage } from "../../../components/ErrorMessage";
import authManager, {
  downloadWithAuth,
  PERMISSIONS,
} from "../../../lib/authManager";
import { DRIVO_URL } from "../../../lib/drivoApollo";
import { T } from "../constants";
import { cx, useUi } from "../styles";
import Panel from "../components/Panel";
import Plate from "../components/Plate";
import Toast from "../components/Toast";
import { formatMoney } from "../helpers";
import { friendlyValetError } from "../dashboard/valetErrors";
import ConfirmPostingModal from "./components/ConfirmPostingModal";
import useChargebackTransactions, {dayRange, PAGE_SIZE} from "./useChargebackTransactions";

const TABS = [
  { key: "FLAGGED", label: "Flagged" },
  { key: "CONFIRMED", label: "Validated" },
];

const CARD_STYLE = {
  boxShadow: "none",
  border: `1px solid ${T.border}`,
  borderRadius: 12,
};

const useStyles = makeStyles({
  root: { maxWidth: 1400, margin: "0 auto" },
  titleBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 6,
  },
  title: { fontSize: 20, fontWeight: 700, color: T.fg, margin: 0 },
  summary: { fontSize: 13, color: T.mutedFg },
  tabs: {
    display: "flex",
    gap: 18,
    margin: "0 0 14px",
    padding: 0,
    listStyle: "none",
    borderBottom: `1px solid ${T.borderSoft}`,
  },
  tab: {
    border: "none",
    background: "transparent",
    color: T.mutedFg,
    padding: "8px 0 10px",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "inherit",
    borderBottom: "2px solid transparent",
    marginBottom: -1,
    '&[aria-selected="true"]': {
      color: T.primary,
      borderBottomColor: T.primary,
      fontWeight: 600,
    },
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 12,
  },
  /* One line: the range reads as a single control, not as wrapping fields. */
  filters: { display: "flex", gap: 6, alignItems: "center", flexWrap: "nowrap" },
  filterLabel: { fontSize: 12, color: T.mutedFg, whiteSpace: "nowrap" },
  control: { height: 30, fontSize: 12, padding: "0 8px" },
  clear: {
    border: "none",
    background: "transparent",
    color: T.mutedFg,
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: 12,
    padding: "0 4px",
  },
  link: {
    border: "none",
    background: "transparent",
    color: T.primary,
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: 12,
    padding: 0,
  },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    textAlign: "left",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: T.subtleFg,
    padding: "12px 16px",
    borderBottom: `1px solid ${T.borderSoft}`,
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  thNum: { textAlign: "right" },
  td: {
    padding: "12px 16px",
    borderBottom: `1px solid ${T.borderSoft}`,
    verticalAlign: "middle",
  },
  total: { fontSize: 12, color: T.fg, fontWeight: 600, marginTop: 2 },
  guest: { fontWeight: 600, color: T.fg },
  muted: { fontSize: 12, color: T.mutedFg },
  status: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontWeight: 600,
    color: T.primary,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 999,
    background: T.primary,
    display: "inline-block",
  },
  dotMuted: { background: T.subtleFg },
  flagged: { color: T.fg },
  actions: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    justifyContent: "flex-end",
    whiteSpace: "nowrap",
  },
  small: { padding: "6px 12px", fontSize: 12 },
  empty: { padding: 24, textAlign: "center", color: T.mutedFg, fontSize: 13 },
  loading: { display: "flex", justifyContent: "center", padding: 40 },
  pager: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
    padding: "10px 16px",
    borderTop: `1px solid ${T.borderSoft}`,
    fontSize: 12,
    color: T.mutedFg,
  },
  pageBtn: { padding: "5px 12px", fontSize: 12 },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    fontSize: 12,
    color: T.mutedFg,
    padding: "10px 16px",
  },
});

/* These are gate events on a finalised booking, so every one of them has
 * already happened - the copy is past tense throughout. */
function formatStay(value, timeZone) {
  if (!value) return null;

  const date = DateTime.fromISO(value, timeZone ? { zone: timeZone } : undefined);
  if (!date.isValid) return null;

  return date.toLocaleString(
    {
      day: "numeric",
      month: "short",
      hour: "numeric",
      minute: "2-digit",
    },
    { locale: "en-AU" },
  );
}

/* The lines that make up the invoice, on one line. A $0 component is left off
 * rather than restated as nothing - the total underneath carries the figure. */
const breakdownText = (row) => {
  const parts = [
    { label: "Parking", amount: row.parkingAud },
    { label: "Overstay", amount: row.overstayAud },
    { label: "Valet fees", amount: row.valetFeesAud },
  ]
    .filter((part) => Number(part.amount) > 0)
    .map((part) => `${part.label} ${formatMoney(part.amount, row.currency)}`);

  return parts.join(" · ") || "Nothing outstanding";
};

export default function ParkingAudit() {
  const s = useStyles();
  const ui = useUi();
  const { installation } = useInstallation();
  const [tab, setTab] = useState("FLAGGED");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [staff, setStaff] = useState("ALL");
  const [confirmRow, setConfirmRow] = useState(null);

  const timeZone = installation?.timeZoneName;

  const {
    rows,
    staff: staffMembers,
    count,
    totalAud,
    currency,
    loading,
    error,
    mutationError,
    confirming,
    toast,
    confirm,
    page,
    pages,
    from: firstRow,
    through,
    hasPrevious,
    hasNext,
    previousPage,
    nextPage,
    refetch,
  } = useChargebackTransactions({
    installationId: installation?.id,
    status: tab,
    from,
    to,
    confirmedById: staff === "ALL" ? null : staff,
    timeZone,
  });

  const canConfirm = authManager.canAccess(
    PERMISSIONS.BOOKINGS_MANAGE,
    installation?.id,
  );
  const flagged = tab === "FLAGGED";

  /* The server has already applied the staff filter, so every row it returns
   * belongs on the page. */
  const visible = rows;

  const onConfirm = async () => {
    const row = confirmRow;
    const ok = await confirm(
      row.id,
      `${row.booking?.plate}: posted to room confirmed · Parking Audit Validated`,
    );
    if (ok) {
      setConfirmRow(null);

      const stepBack = page > 0 && rows.length === 1;
      if (stepBack) {
        previousPage();
        await refetch({ offset: (page - 1) * PAGE_SIZE });
      } else {
        refetch();
      }
    }
  };

  /* Built by the API, not from the rows on screen: the export covers every
   * page of the current filters, and the page only ever holds one of them. */
  const exportCsv = async () => {
    const params = new URLSearchParams({ status: tab });

    const range = dayRange(from, to, timeZone);

    if (installation?.id) params.set("installationId", installation.id);
    if (range.from) params.set("from", range.from);
    if (range.to) params.set("to", range.to);
    if (staff !== "ALL") params.set("confirmedById", staff);

    const today = (timeZone ? DateTime.now().setZone(timeZone) : DateTime.now())
      .toISODate();

    await downloadWithAuth({
      url: `${DRIVO_URL}/chargeback-transactions/download?${params}`,
      filename: `parking-audit-${today}.csv`,
    });
  };

  return (
    <div className={s.root}>
      <div className={s.titleBar}>
        <h1 className={s.title}>Parking Audit</h1>
        {/* Both figures speak for the set being fetched, so neither is shown
            until that set has arrived. */}
        <span className={s.summary}>
          {loading ? null : (
            <>
              {count} {flagged ? "flagged" : "validated"} ·{" "}
              {formatMoney(totalAud, currency)}{" "}
              {flagged ? "at risk" : "posted"}
            </>
          )}
        </span>
      </div>

      <ul className={s.tabs} role="tablist" aria-label="Parking Audit view">
        {TABS.map((item) => (
          <li key={item.key}>
            <button
              type="button"
              role="tab"
              className={s.tab}
              aria-selected={tab === item.key}
              onClick={() => setTab(item.key)}
            >
              {item.label}
            </button>
          </li>
        ))}
      </ul>

      {error ? <GraphqlErrorMessage error={error} /> : null}
      {mutationError ? (
        <GraphqlErrorMessage
          error={{
            message: friendlyValetError(mutationError, "confirm this posting"),
          }}
        />
      ) : null}

      <div className={s.toolbar}>
        <div className={s.filters} role="group" aria-label="Parking Audit filters">
          <span className={s.filterLabel}>Departed</span>
          <input
            type="date"
            className={cx(ui.field, s.control)}
            value={from}
            max={to || undefined}
            onChange={(event) => setFrom(event.target.value)}
            aria-label="Departed from"
          />
          <span className={s.filterLabel}>to</span>
          <input
            type="date"
            className={cx(ui.field, s.control)}
            value={to}
            min={from || undefined}
            onChange={(event) => setTo(event.target.value)}
            aria-label="Departed to"
          />
          {from || to ? (
            <button
              type="button"
              className={s.clear}
              onClick={() => {
                setFrom("");
                setTo("");
              }}
            >
              Clear
            </button>
          ) : null}
          <select
            className={cx(ui.field, s.control)}
            value={staff}
            onChange={(event) => setStaff(event.target.value)}
            aria-label="Staff filter"
          >
            <option value="ALL">All staff</option>
            {staffMembers.map((member) => (
              <option key={member.id} value={member.id}>
                {member.name}
              </option>
            ))}
          </select>
        </div>
        {canConfirm ? (
          <button type="button" className={s.link} onClick={exportCsv}>
            Export CSV
          </button>
        ) : null}
      </div>

      <Panel style={CARD_STYLE}>
        {loading ? (
          /* Inside the card only: the heading, tabs and filters stay put, so
             switching tabs never blanks the page out from under the user. */
          <div className={s.loading} aria-busy="true">
            <LoadingIndicator />
          </div>
        ) : visible.length === 0 ? (
          <div className={s.empty}>
            No chargebacks match the current filters.
          </div>
        ) : (
          <div className={s.tableWrap}>
            <table className={s.table} aria-label="Parking Audit bookings">
              <thead>
                <tr>
                  <th className={s.th}>Guest</th>
                  <th className={s.th}>Plate</th>
                  <th className={s.th}>Guest card</th>
                  <th className={s.th}>
                    {flagged ? "Hotel to post" : "Posted to room"}
                  </th>
                  <th className={s.th}>Stay / departure</th>
                  <th className={s.th}>Status</th>
                  <th className={s.th}>Confirmed by</th>
                  <th className={cx(s.th, s.thNum)}>Action</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => {
                  const booking = row.booking || {};
                  const arrived = formatStay(row.arrivedAt, timeZone);
                  const departed = formatStay(row.departedAt, timeZone);

                  return (
                    <tr key={row.id} aria-label={`${flagged ? "Flagged" : "Validated"} ${booking.plate}`}>
                      <td className={s.td}>
                        <div className={s.guest}>{booking.guestName || "-"}</div>
                        {booking.guestRoom ? (
                          <div className={s.muted}>Room {booking.guestRoom}</div>
                        ) : null}
                      </td>
                      <td className={s.td}>
                        <Plate size={12}>{booking.plate || "-"}</Plate>
                      </td>
                      <td className={s.td}>{booking.rateCard?.name || "-"}</td>
                      <td className={s.td}>
                        {breakdownText(row)}
                        <div className={s.total}>
                          {flagged ? "Hotel to post" : "Posted to room"} ·{" "}
                          {formatMoney(row.totalAud, row.currency)}
                        </div>
                      </td>
                      <td className={s.td}>
                        {arrived ? `Arrived ${arrived}` : "No entry recorded"}
                        <div className={s.muted}>
                          {departed ? `departed ${departed}` : "no exit recorded"}
                        </div>
                      </td>
                      <td className={s.td}>
                        <span className={cx(s.status, flagged && s.flagged)}>
                          <span className={cx(s.dot, flagged && s.dotMuted)} />
                          {flagged ? "Flagged" : "Validated"}
                        </span>
                        <div className={s.muted}>
                          {flagged ? "Room posting not confirmed" : "Posted to room"}
                        </div>
                      </td>
                      <td className={s.td}>
                        {row.confirmedByName ? (
                          <>
                            {row.confirmedByName}
                            <div className={s.muted}>{row.confirmedRole}</div>
                          </>
                        ) : (
                          <span className={s.muted}>—</span>
                        )}
                      </td>
                      <td className={cx(s.td, s.actions)}>
                        {flagged && canConfirm ? (
                          <button
                            type="button"
                            className={cx(ui.primaryBtn, s.small)}
                            onClick={() => setConfirmRow(row)}
                          >
                            Posted to room
                          </button>
                        ) : null}
                        {flagged && !canConfirm ? (
                          <span className={s.muted}>View only</span>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && count > 0 ? (
          <div className={s.pager}>
            <span>
              {firstRow}–{through} of {count}
            </span>
            <button
              type="button"
              className={cx(ui.ghostBtn, s.pageBtn)}
              onClick={previousPage}
              disabled={!hasPrevious || loading}
            >
              Previous
            </button>
            <span>
              Page {page + 1} of {pages}
            </span>
            <button
              type="button"
              className={cx(ui.ghostBtn, s.pageBtn)}
              onClick={nextPage}
              disabled={!hasNext || loading}
            >
              Next
            </button>
          </div>
        ) : null}
      </Panel>

      {confirmRow ? (
        <ConfirmPostingModal
          row={confirmRow}
          hotelName={installation?.name}
          staffName={authManager.userName}
          staffRole={authManager.currentRoleAt(installation?.id)}
          canConfirm={canConfirm}
          submitting={confirming}
          onConfirm={onConfirm}
          onClose={() => setConfirmRow(null)}
        />
      ) : null}

      {toast ? <Toast>{toast}</Toast> : null}
    </div>
  );
}
